import { useEffect, useRef } from 'react';
import { useSimulationStore } from '../../stores/useSimulationStore';
import styles from './Simulation.module.css';

export function SimulationControls() {
  const result = useSimulationStore(s => s.result);
  const currentStep = useSimulationStore(s => s.currentStep);
  const playing = useSimulationStore(s => s.playing);
  const playbackMode = useSimulationStore(s => s.playbackMode);
  const speed = useSimulationStore(s => s.speed);
  const inputs = useSimulationStore(s => s.inputs);
  const inputValues = useSimulationStore(s => s.inputValues);

  const run = useSimulationStore(s => s.run);
  const stepForward = useSimulationStore(s => s.stepForward);
  const stepBack = useSimulationStore(s => s.stepBack);
  const play = useSimulationStore(s => s.play);
  const pause = useSimulationStore(s => s.pause);
  const reset = useSimulationStore(s => s.reset);
  const setSpeed = useSimulationStore(s => s.setSpeed);
  const setPlaybackMode = useSimulationStore(s => s.setPlaybackMode);
  const setInputValue = useSimulationStore(s => s.setInputValue);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-play timer
  useEffect(() => {
    if (!playing || !result) return;
    if (currentStep >= result.steps.length - 1) {
      pause();
      return;
    }

    timerRef.current = setTimeout(() => {
      stepForward();
    }, speed);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [playing, currentStep, speed, result, stepForward, pause]);

  const hasResult = result !== null;
  const isFinished = hasResult && currentStep >= result.steps.length - 1;
  const canStepForward = hasResult && currentStep < result.steps.length - 1;
  const canStepBack = hasResult && currentStep >= 0;

  return (
    <div>
      {/* Input fields */}
      {inputs.length > 0 && (
        <div className={styles.inputsSection}>
          <div className={styles.inputsSectionTitle}>Simulation Inputs</div>
          {inputs.map(inp => (
            <div key={inp.name} className={styles.inputRow}>
              <label className={styles.inputLabel} title={inp.name}>
                {inp.name}
              </label>
              {inp.type === 'boolean' ? (
                <input
                  type="checkbox"
                  checked={inputValues[inp.name] === 'true'}
                  onChange={e => setInputValue(inp.name, e.target.checked ? 'true' : 'false')}
                  disabled={hasResult}
                />
              ) : (
                <input
                  className={styles.inputField}
                  type={inp.type === 'number' ? 'number' : 'text'}
                  value={inputValues[inp.name] ?? inp.defaultValue}
                  onChange={e => setInputValue(inp.name, e.target.value)}
                  placeholder={inp.defaultValue}
                  disabled={hasResult}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Control buttons */}
      <div className={styles.controls}>
        {!hasResult ? (
          <button className={`${styles.controlBtn} ${styles.controlBtnPrimary}`} onClick={run}>
            ▶ Run Simulation
          </button>
        ) : (
          <>
            {playbackMode === 'auto' ? (
              playing ? (
                <button className={styles.controlBtn} onClick={pause}>
                  ⏸ Pause
                </button>
              ) : (
                <button
                  className={`${styles.controlBtn} ${styles.controlBtnPrimary}`}
                  onClick={play}
                  disabled={isFinished}
                >
                  ▶ Play
                </button>
              )
            ) : null}

            <button className={styles.controlBtn} onClick={stepBack} disabled={!canStepBack}>
              ◀ Back
            </button>
            <button className={styles.controlBtn} onClick={stepForward} disabled={!canStepForward}>
              Step ▶
            </button>
            <button className={`${styles.controlBtn} ${styles.controlBtnDanger}`} onClick={reset}>
              ↺ Reset
            </button>
          </>
        )}

        {/* Playback mode toggle */}
        <div className={styles.playbackToggle}>
          <button
            className={`${styles.playbackBtn} ${playbackMode === 'auto' ? styles.playbackBtnActive : ''}`}
            onClick={() => setPlaybackMode('auto')}
          >
            Auto
          </button>
          <button
            className={`${styles.playbackBtn} ${playbackMode === 'step' ? styles.playbackBtnActive : ''}`}
            onClick={() => setPlaybackMode('step')}
          >
            Step
          </button>
        </div>

        {/* Speed control */}
        {playbackMode === 'auto' && (
          <div className={styles.speedControl}>
            <span>Fast</span>
            <input
              type="range"
              className={styles.speedSlider}
              min={50}
              max={2000}
              step={50}
              value={2050 - speed}
              onChange={e => setSpeed(2050 - parseInt(e.target.value, 10))}
            />
            <span>Slow</span>
          </div>
        )}
      </div>
    </div>
  );
}
